import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';

import { AspectOption } from '../../shared/models/feedback-aspect.model';
import { FeedbackRequest } from '../../shared/models/request/feedback-request.model';

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './feedback-form.component.html',
  styleUrls: ['./feedback-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeedbackFormComponent {
  readonly userId = input<number | null>(null);
  readonly submitted = output<FeedbackRequest>();

  private readonly fb = inject(FormBuilder);

  readonly hoveredStar = signal<number | null>(null);
  readonly currentRating = signal<number>(0);
  readonly selectedBestAspect = signal<AspectOption | null>(null);
  readonly selectedNeedsImprovement = signal<AspectOption | null>(null);

  readonly stars = [1, 2, 3, 4, 5] as const;
  readonly bestFeatureOptions: readonly AspectOption[] = [
    'Usability',
    'Performance',
    'Design',
    'Features',
    'Clarity of Information'
  ] as const;
  readonly improvementOptions: readonly AspectOption[] = [
    'Usability',
    'Performance',
    'Design',
    'Features',
    'Clarity of Information'
  ] as const;

  readonly displayRating = computed(() => this.hoveredStar() ?? this.currentRating());

  readonly isFormValid = computed(() => {
    return this.currentRating() > 0;
  });

  readonly form = this.fb.group({
    comments: new FormControl<string>('', { nonNullable: true }),
    isAnonymous: new FormControl<boolean>(false, { nonNullable: true })
  });

  setRating(star: number): void {
    this.currentRating.set(star);
  }

  handleStarHover(star: number | null): void {
    this.hoveredStar.set(star);
  }

  toggleBestAspect(value: AspectOption): void {
    this.selectedBestAspect.update((current) => (current === value ? null : value));
  }

  toggleNeedsImprovement(value: AspectOption): void {
    this.selectedNeedsImprovement.update((current) => (current === value ? null : value));
  }

  onSubmit(): void {
    if (!this.isFormValid()) return;

    const isAnonymous = this.form.controls.isAnonymous.value;
    const comments = this.form.controls.comments.value.trim();
    const submittedValue: FeedbackRequest = {
      overallSatisfaction: this.currentRating(),
      bestAspect: this.selectedBestAspect(),
      needsImprovement: this.selectedNeedsImprovement(),
      comments: comments || null,
      userId: isAnonymous ? null : (this.userId() ?? null)
    };

    this.submitted.emit(submittedValue);
  }
}
